import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { catchError, EMPTY, finalize, Subject, switchMap, tap } from "rxjs";
import { FilterMetadata } from "primeng/api";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { AccessApprovalDto } from "../../../core/models/client-group/access-approval.dto";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";
import { ClientGroupService } from "../../../core/services/client-group.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";

/** Administrator queue and history; the backend owns decisions and delayed dispatch. */
@Component({
  selector: "jni-access-approval",
  imports: [
    CommonModule,
    TableModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    TagModule,
    SpinnerComponent,
  ],
  templateUrl: "./access-approval.component.html",
  styleUrl: "./access-approval.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessApprovalComponent {
  readonly accessApprovals = signal<AccessApprovalDto[]>([]);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly hasLoaded = signal(false);
  readonly errorMessage = signal("");
  readonly decidingIds = signal<ReadonlySet<number>>(new Set());
  readonly initialFilters: Record<string, FilterMetadata> = {};
  readonly tableFilters = signal<Record<string, FilterMetadata>>({});
  readonly columns = [
    { field: "customerAccount", label: "Client account", matchMode: "equals" },
    { field: "clientName", label: "Client name", matchMode: "contains" },
    { field: "projectId", label: "Project ID", matchMode: "equals" },
    { field: "email", label: "Access email", matchMode: "contains" },
  ];
  private readonly service = inject(ClientGroupService);
  private readonly toastr = inject(JniToastrService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loads = new Subject<TableLazyLoadEvent>();
  private lastEvent: TableLazyLoadEvent = {
    first: 0,
    rows: 25,
    filters: this.initialFilters,
  };

  constructor() {
    // Cancel stale requests so a slower response cannot replace the newest page/filter.
    this.loads
      .pipe(
        switchMap(event => {
          this.loading.set(true);
          this.errorMessage.set("");
          const filters: GenericFilterRequestDto[] = [];
          const activeFilters =
            Object.keys(event.filters ?? {}).length > 0
              ? (event.filters ?? {})
              : this.tableFilters();
          for (const [field, metadata] of Object.entries(activeFilters)) {
            const meta = Array.isArray(metadata) ? metadata[0] : metadata;
            if (typeof meta?.value === "string" && meta.value.trim()) {
              filters.push({
                columnName: field,
                value: meta.value.trim(),
                matchMode: meta.matchMode ?? "contains",
                tableName: "",
              });
            }
          }
          // Sorting targets entity fields, not the DTO-only requestedAt/requestedBy aliases.
          const sort =
            typeof event.sortField === "string"
              ? [
                  {
                    field: event.sortField,
                    order: event.sortOrder === 1 ? 0 : 1,
                  },
                ]
              : [{ field: "Id", order: 1 }];
          return this.service
            .listAccessApprovals(
              event.first ?? 0,
              event.rows ?? 25,
              filters,
              sort
            )
            .pipe(
              tap(response => {
                this.accessApprovals.set(response.data ?? []);
                this.totalRecords.set(response.totalRecords ?? 0);
              }),
              catchError(() => {
                this.accessApprovals.set([]);
                this.totalRecords.set(0);
                this.errorMessage.set(
                  "Unable to load access approvals. Please retry."
                );
                return EMPTY;
              }),
              finalize(() => {
                this.hasLoaded.set(true);
                this.loading.set(false);
              })
            );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  loadData(event: TableLazyLoadEvent): void {
    this.lastEvent = event;
    this.loads.next(event);
  }

  refresh(): void {
    this.loadData(this.lastEvent);
  }

  onColumnFilter(field: string, matchMode: string, value: string): void {
    const nextFilters = {
      ...this.tableFilters(),
      [field]: {
        value,
        matchMode,
      },
    };

    this.tableFilters.set(nextFilters);
    this.loadData({
      ...this.lastEvent,
      first: 0,
      filters: nextFilters,
    });
  }

  approve(row: AccessApprovalDto): void {
    this.decide(row, "approve");
  }
  reject(row: AccessApprovalDto): void {
    this.decide(row, "reject");
  }

  private decide(row: AccessApprovalDto, decision: "approve" | "reject"): void {
    if (
      row.status !== "Pending" ||
      this.loading() ||
      this.decidingIds().has(row.id)
    )
      return;
    this.decidingIds.update(ids => new Set([...ids, row.id]));
    this.service
      .decideAccessApproval(row.id, decision)
      .pipe(
        finalize(() =>
          this.decidingIds.update(
            ids => new Set([...ids].filter(id => id !== row.id))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.accessApprovals.update(rows =>
            rows.map(item =>
              item.id === row.id
                ? {
                    ...item,
                    status: decision === "approve" ? "Approved" : "Rejected",
                  }
                : item
            )
          );
          this.toastr.info(
            "Access request",
            decision === "approve"
              ? "Access request approved."
              : "Access request rejected. No invitation will be sent."
          );
          this.refresh();
        },
        error: (error: unknown) => {
          this.toastr.error(
            "Unable to update request",
            error instanceof Error
              ? error.message
              : "Please refresh and try again."
          );
          this.refresh();
        },
      });
  }
}
